// Sales → Accounts: every customer, searchable, filterable by status.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fill } from "../../i18n/dictionary";
import type { SalesAccount } from "../../data/sales";
import type { OrgLifecycle } from "../../data/types";
import { Card, Chip, ErrorPanel, Field, INPUT, LoadingLine, TableCard, TD, TH, formatMoney, useLoad } from "../admin/ui";
import { SectionTitle, salesErrorCopy, statusChip, useSales } from "./shared";

/** Lower-cased haystack an account is searched by. */
export function accountHaystack(a: SalesAccount): string {
  return [a.name, a.legalName, a.contactName, a.contactEmail, a.salesOwner, ...a.adminEmails].filter(Boolean).join(" ").toLowerCase();
}

export function filterAccounts(list: SalesAccount[], q: string, status: OrgLifecycle | "all"): SalesAccount[] {
  const needle = q.trim().toLowerCase();
  return list.filter((a) => (status === "all" || a.status === status) && (!needle || accountHaystack(a).includes(needle)));
}

export function AccountsList() {
  const { ds, t, locale } = useSales();
  const load = useLoad(() => ds.accounts(), [ds]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<OrgLifecycle | "all">("all");
  const shown = useMemo(() => filterAccounts(load.data ?? [], q, status), [load.data, q, status]);

  return (
    <section aria-labelledby="accounts-title">
      <SectionTitle id="accounts-title">{t.accounts.title}</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 10, marginBottom: 14, maxWidth: 640 }}>
        <Field label={t.accounts.search} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.accounts.searchHint} />
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label htmlFor="acc-status" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.accounts.statusFilter}
          </label>
          <select id="acc-status" value={status} onChange={(e) => setStatus(e.target.value as OrgLifecycle | "all")} style={INPUT}>
            <option value="all">{t.accounts.statusAll}</option>
            <option value="active">{t.common.stActive}</option>
            <option value="suspended">{t.common.stSuspended}</option>
            <option value="closed">{t.common.stClosed}</option>
          </select>
        </div>
      </div>

      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : (
        <>
          <p role="status" aria-live="polite" style={{ margin: "0 0 10px", fontSize: 14, color: "var(--ink-soft)" }}>
            {fill(t.accounts.count, { n: shown.length, total: load.data.length })}
          </p>
          {shown.length === 0 ? (
            <Card>
              <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.accounts.none}</p>
            </Card>
          ) : (
            <TableCard label={t.accounts.title} minWidth={820}>
              <thead>
                <tr>
                  <th scope="col" style={TH}>{t.accounts.colName}</th>
                  <th scope="col" style={TH}>{t.accounts.colStatus}</th>
                  <th scope="col" style={TH}>{t.accounts.colContact}</th>
                  <th scope="col" style={TH}>{t.accounts.colLicences}</th>
                  <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.accounts.colPrice}</th>
                  <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.accounts.colOutstanding}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => {
                  const st = statusChip(t, a.status);
                  return (
                    <tr key={a.orgId}>
                      <td style={TD}>
                        <Link to={`/sales/accounts/${encodeURIComponent(a.orgId)}`} style={{ color: "var(--brand)", fontWeight: 700 }}>
                          {a.name}
                        </Link>
                        <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-faint)" }}>
                          {a.kind === "individual" ? t.common.kindIndividual : t.common.kindOrg}
                        </span>
                      </td>
                      <td style={TD}>
                        <Chip tone={st.tone}>{st.label}</Chip>
                      </td>
                      <td style={{ ...TD, fontSize: 13.5, overflowWrap: "anywhere", minWidth: 160 }}>
                        {a.contactName ?? t.common.none}
                        {a.contactEmail && <span style={{ display: "block", color: "var(--ink-faint)" }}>{a.contactEmail}</span>}
                      </td>
                      <td style={{ ...TD, fontSize: 13.5 }}>
                        {fill(t.accounts.licencesLine, { live: a.licencesLive, claimed: a.licencesClaimed, allowance: a.familyAllowance })}
                      </td>
                      <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{formatMoney(locale, a.familyPriceMonthly, a.currency)}</td>
                      <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: a.outstanding > 0 ? 700 : 400 }}>
                        {formatMoney(locale, a.outstanding, a.currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TableCard>
          )}
        </>
      )}
    </section>
  );
}
